import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Paper,
  Divider,
  alpha,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore,
  Add,
  Edit,
  Delete,
  VideoLibrary,
  Quiz,
  LibraryBooks,
  Save,
  CloudUpload,
  Warning,
  CheckCircle,
} from "@mui/icons-material";
import axios from "axios";
import Swal from "sweetalert2";

const GOLD = "#D4AF37";
const API_BASE_URL = "https://tdtlworld.com/dss-kaas-backend/";

export default function Curriculum({ courseId }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const token = localStorage.getItem("jwttoken");

  // Modals & Forms State
  const [sectionDialog, setSectionDialog] = useState({ open: false, edit: false, data: { title: "", description: "" } });
  const [subpartDialog, setSubpartDialog] = useState({ open: false, edit: false, sectionId: null, data: { title: "", description: "", video: null } });
  const [activeTabs, setActiveTabs] = useState({}); // Tracking tabs per section: { sectionId: 0 }

  const fetchCourseData = useCallback(async () => {
    if (!courseId) return;
    setFetching(true);
    try {
      const resp = await axios.get(`${API_BASE_URL}lms/courses/${courseId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(resp.data.parts || []);
    } catch (err) {
      console.error("Fetch failed", err);
    } finally {
      setFetching(false);
    }
  }, [courseId, token]);

  useEffect(() => {
    fetchCourseData();
  }, [fetchCourseData]);

  // Section Handlers
  const handleSaveSection = async () => {
    if (!sectionDialog.data.title) return;
    setLoading(true);
    try {
      const payload = {
        course: courseId,
        part_title: sectionDialog.data.title,
        part_description: sectionDialog.data.description,
        part_number: sections.length + 1
      };

      if (sectionDialog.edit) {
        await axios.patch(`${API_BASE_URL}lms/courseparts/${courseId}/${sectionDialog.id}/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_BASE_URL}lms/courseparts/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setSectionDialog({ open: false, edit: false, data: { title: "", description: "" } });
      fetchCourseData();
      Swal.fire({ icon: "success", title: "Saved", showConfirmButton: false, timer: 1500 });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed to save section" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = (id) => {
    Swal.fire({
      title: "Delete Section?",
      text: "All subparts and quizzes in this section will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${API_BASE_URL}lms/courseparts/${courseId}/${id}/`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          fetchCourseData();
        } catch (err) {
          Swal.fire("Error", "Failed to delete section", "error");
        }
      }
    });
  };

  // Subpart Handlers
  const handleSaveSubpart = async () => {
    if (!subpartDialog.data.title) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("course_part", subpartDialog.sectionId);
      formData.append("subpart_title", subpartDialog.data.title);
      formData.append("part_description", subpartDialog.data.description);
      if (subpartDialog.data.video) {
        formData.append("video", subpartDialog.data.video);
      }

      if (subpartDialog.edit) {
        await axios.patch(`${API_BASE_URL}lms/subparts/${subpartDialog.id}/`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
      } else {
        await axios.post(`${API_BASE_URL}lms/subparts/`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
      }
      setSubpartDialog({ open: false, edit: false, sectionId: null, data: { title: "", description: "", video: null } });
      fetchCourseData();
      Swal.fire({ icon: "success", title: "Subpart Saved", showConfirmButton: false, timer: 1500 });
    } catch (err) {
      Swal.fire("Error", "Failed to save subpart", "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Curriculum</Typography>
          <Typography variant="body2" color="text.secondary">Start building your course structure by adding sections and lectures.</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setSectionDialog({ open: true, edit: false, data: { title: "", description: "" } })}
          sx={{ bgcolor: GOLD, borderRadius: "50px", fontWeight: 800, "&:hover": { bgcolor: alpha(GOLD, 0.8) } }}
        >
          Add Section
        </Button>
      </Stack>

      {sections.length === 0 ? (
        <Paper sx={{ p: 10, textAlign: "center", borderRadius: 4, bgcolor: alpha(GOLD, 0.05), border: "2px dashed", borderColor: alpha(GOLD, 0.2) }}>
          <LibraryBooks sx={{ fontSize: 60, color: alpha(GOLD, 0.3), mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No sections added yet</Typography>
          <Button sx={{ mt: 2, color: GOLD }} onClick={() => setSectionDialog({ open: true, edit: false, data: { title: "", description: "" } })}>Create your first section</Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {sections.map((section, idx) => (
            <Accordion 
              key={section.id} 
              sx={{ 
                borderRadius: "16px !important", 
                border: "1px solid", 
                borderColor: "divider",
                "&:before": { display: "none" },
                boxShadow: "none",
                overflow: "hidden"
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%", pr: 2 }}>
                  <Typography fontWeight={800} sx={{ minWidth: 100 }}>Section {idx + 1}</Typography>
                  <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>{section.part_title}</Typography>
                  <Stack direction="row" spacing={1}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSectionDialog({ open: true, edit: true, id: section.id, data: { title: section.part_title, description: section.part_description } }); }}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, borderTop: "1px solid", borderColor: "divider" }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs 
                    value={activeTabs[section.id] || 0} 
                    onChange={(e, val) => setActiveTabs({ ...activeTabs, [section.id]: val })}
                    sx={{ px: 3 }}
                  >
                    <Tab label="Content" />
                    <Tab label="Quizzes" />
                  </Tabs>
                </Box>
                
                <Box sx={{ p: 3 }}>
                  {(activeTabs[section.id] || 0) === 0 ? (
                    <Stack spacing={2}>
                      {(section.subparts || []).map((sub, sidx) => (
                        <Paper key={sub.id} variant="outlined" sx={{ p: 2, borderRadius: 3, display: "flex", alignItems: "center", gap: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, bgcolor: alpha(GOLD, 0.1), borderRadius: 2, color: GOLD }}>
                            <VideoLibrary />
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>Lecture {sidx + 1}: {sub.subpart_title}</Typography>
                            {sub.video && <Chip label="Video Attached" size="small" color="success" variant="outlined" sx={{ mt: 0.5 }} />}
                          </Box>
                          <Stack direction="row">
                            <IconButton size="small" onClick={() => setSubpartDialog({ open: true, edit: true, id: sub.id, sectionId: section.id, data: { title: sub.subpart_title, description: sub.part_description, video: null } })}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Paper>
                      ))}
                      <Button
                        startIcon={<Add />}
                        fullWidth
                        onClick={() => setSubpartDialog({ open: true, edit: false, sectionId: section.id, data: { title: "", description: "", video: null } })}
                        sx={{ py: 1.5, border: "2px dashed", borderColor: "divider", borderRadius: 3, color: "text.secondary" }}
                      >
                        Add Lecture
                      </Button>
                    </Stack>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Quiz sx={{ fontSize: 40, color: "divider", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">Quiz management coming soon</Typography>
                      <Button variant="text" size="small" sx={{ mt: 1, color: GOLD }}>Add Quiz</Button>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      {/* Section Dialog */}
      <Dialog open={sectionDialog.open} onClose={() => setSectionDialog({ ...sectionDialog, open: false })} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>{sectionDialog.edit ? "Edit Section" : "Add Section"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Section Title"
            placeholder="e.g. Introduction to the course"
            value={sectionDialog.data.title}
            onChange={(e) => setSectionDialog({ ...sectionDialog, data: { ...sectionDialog.data, title: e.target.value } })}
            sx={{ mb: 3, mt: 1 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="What should students be able to do at the end of this section?"
            value={sectionDialog.data.description}
            onChange={(e) => setSectionDialog({ ...sectionDialog, data: { ...sectionDialog.data, description: e.target.value } })}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setSectionDialog({ ...sectionDialog, open: false })}>Cancel</Button>
          <Button variant="contained" disabled={loading} onClick={handleSaveSection} sx={{ bgcolor: GOLD, borderRadius: "50px", px: 4, fontWeight: 700 }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : "Save Section"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subpart Dialog */}
      <Dialog open={subpartDialog.open} onClose={() => setSubpartDialog({ ...subpartDialog, open: false })} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>{subpartDialog.edit ? "Edit Lecture" : "Add New Lecture"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Lecture Title"
            value={subpartDialog.data.title}
            onChange={(e) => setSubpartDialog({ ...subpartDialog, data: { ...subpartDialog.data, title: e.target.value } })}
            sx={{ mb: 3, mt: 1 }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={subpartDialog.data.description}
            onChange={(e) => setSubpartDialog({ ...subpartDialog, data: { ...subpartDialog.data, description: e.target.value } })}
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ p: 3, border: "2px dashed", borderColor: "divider", borderRadius: 4, textAlign: "center", bgcolor: alpha(GOLD, 0.02) }}>
            <input
              type="file"
              accept="video/*"
              style={{ display: "none" }}
              id="video-upload"
              onChange={(e) => setSubpartDialog({ ...subpartDialog, data: { ...subpartDialog.data, video: e.target.files[0] } })}
            />
            <label htmlFor="video-upload">
              <Button component="span" variant="outlined" startIcon={<CloudUpload />} sx={{ borderColor: GOLD, color: GOLD, borderRadius: "50px", mb: 1 }}>
                {subpartDialog.data.video ? "Change Video" : "Upload Video"}
              </Button>
            </label>
            <Typography variant="caption" display="block" color="text.secondary">
              {subpartDialog.data.video ? `Selected: ${subpartDialog.data.video.name}` : "MP4, MOV supported. Max 500MB."}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setSubpartDialog({ ...subpartDialog, open: false })}>Cancel</Button>
          <Button variant="contained" disabled={loading} onClick={handleSaveSubpart} sx={{ bgcolor: GOLD, borderRadius: "50px", px: 4, fontWeight: 700 }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : "Save Lecture"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
